"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ArrowRight, Loader } from "lucide-react";
function ClubForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center gap-6 py-8">
        <svg className="size-16 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
        animationDuration: "3s"
      }}>
          <circle cx="50" cy="50" r="48" stroke="var(--foreground)" strokeOpacity="0.3" strokeWidth="1" />
          <circle cx="50" cy="50" r="38" stroke="var(--foreground)" strokeOpacity="0.25" strokeWidth="1" />
          <circle cx="50" cy="50" r="28" stroke="var(--foreground)" strokeOpacity="0.2" strokeWidth="1" />
          <circle cx="50" cy="50" r="18" fill="var(--primary)" />
          <circle cx="50" cy="50" r="4" fill="var(--background)" />
        </svg>
        <p className="font-default italic text-2xl md:text-3xl text-foreground text-center">
          Request received. We will be in touch shortly.
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-8">
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-3 items-end">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 w-full">
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="INSTITUTIONAL.EMAIL@SCHOOL.EDU" className="h-12 bg-transparent border-0 border-b border-border px-0 py-2 font-mono uppercase text-sm tracking-wider text-foreground placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-0 focus-visible:border-b-2 transition-colors" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-xs uppercase tracking-wider" />
            </FormItem>} />

        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="hidden">
              <FormControl>
                <Input type="hidden" {...field} />
              </FormControl>
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className="h-12 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-white hover:text-primary font-mono uppercase text-xs tracking-widest font-semibold shadow-md w-full sm:w-auto transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending
            </> : <>
              Book a pilot call <ArrowRight className="size-4" />
            </>}
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        <span>Diagnostic Insights</span>
        <span className="text-primary">·</span>
        <span>Process Mapping</span>
        <span className="text-primary">·</span>
        <span>Data Privacy</span>
      </div>

      <FormField control={control} name="consent" rules={{
      required: "Please confirm to receive updates"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 justify-center">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground" />
            </FormControl>
            <div className="space-y-1">
              <FormLabel className="text-sm text-muted-foreground font-normal cursor-pointer">
                I agree to receive technical updates and pilot information.
              </FormLabel>
              <FormMessage className="font-mono text-xs uppercase tracking-wider" />
            </div>
          </FormItem>} />

      <FormField control={control} name="favorite_genre" render={({
      field
    }) => <FormItem className="hidden">
            <FormControl>
              <Input type="hidden" {...field} />
            </FormControl>
          </FormItem>} />

      <FormField control={control} name="address" render={({
      field
    }) => <FormItem className="hidden">
            <FormControl>
              <Input type="hidden" {...field} />
            </FormControl>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-sm bg-destructive/10">
          <p className="text-destructive text-sm font-mono uppercase tracking-wider text-center">{submitError}</p>
        </div>}
    </Form>;
}
export default function Newsletter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="464" id="newsletter" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-4">
      <div className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-luminosity" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/diagnostic_engine_blueprint.webp)"
    }} aria-hidden="true" />

      <div className="absolute inset-0 bg-background/20 pointer-events-none" aria-hidden="true" />

      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <svg className="w-[800px] h-[800px] md:w-[1000px] md:h-[1000px] opacity-[0.05]" viewBox="0 0 1000 1000" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
        animation: "spin 60s linear infinite"
      }}>
          {Array.from({
          length: 24
        }).map((_, i) => <circle key={i} data-index={i} cx="500" cy="500" r={60 + i * 18} stroke="var(--foreground)" strokeWidth="0.8" fill="none" />)}
          <circle cx="500" cy="500" r="80" fill="var(--primary)" opacity="0.4" />
          <circle cx="500" cy="500" r="10" fill="var(--background)" />
        </svg>
      </div>

      <div className="absolute inset-0 bg-background/40 pointer-events-none" aria-hidden="true" />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div ref={ref} className={`relative z-10 max-w-[720px] mx-auto flex flex-col items-center text-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="inline-block mb-10 px-5 py-2 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] font-bold rounded-sm">
           Institutional Inquiry 
        </div>

        <h2 className="font-default text-foreground leading-[0.95] mb-8 tracking-tight">
          <span className="block text-6xl md:text-8xl lg:text-9xl font-medium">
            Start the
          </span>
          <span className="block text-6xl md:text-8xl lg:text-9xl font-medium italic text-primary">
            Conversation.
          </span>
        </h2>

        <p className="text-base md:text-[17px] leading-relaxed text-muted-foreground max-w-[540px] mb-12 font-default">
          Connect with our team to discuss how Setura can map your assessment data to specific process failures and improve student outcomes.
        </p>

        <div className="w-full">
          <FormProvider formKey="newsletter_signup" formId="newsletter_signup" sectionName="newsletter" defaultValues={{
          name: "School Lead",
          email: "",
          favorite_genre: "Other",
          address: "",
          consent: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          consent: {
            required: true
          }
        }} submitText="Book a pilot call" successMessage="Request received. We will be in touch shortly." errorMessage="Something went wrong. Please try again.">
            <ClubForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}